// Date/time formatting helpers.
//
// All formatters render in the user's local timezone (the same zone reported in
// the `X-Client-Time-Zone` request header). Backend timestamps are UTC ISO
// strings, so `new Date(iso)` parses them as UTC and the local getters below
// convert to the user's timezone. Never use `.toISOString()` for display — it
// always emits UTC and ignores the user's timezone.

const DASH = '—';

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Standard datetime display: `YYYY-MM-DD HH:MM` in the user's local timezone.
 * Use this for all timestamp columns/fields unless a feature has a deliberate
 * reason to differ. Returns `—` for empty/invalid input.
 */
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

/** Date only: `YYYY-MM-DD` in the user's local timezone. */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Long datetime display: `19 Aug 2026 07:00` in the user's local timezone.
 * Used where a schedule instant reads as prose (auto-dispatch slots) rather
 * than as a sortable table timestamp. Returns `—` for empty/invalid input.
 */
export const formatDateTimeLong = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return (
    `${d.getDate()} ${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};
